import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { FormNotice, PasswordRules } from '@/components/auth-shell';
import { Button, Card, CardHeader, Field, Input, PageHeader, PasswordInput } from '@/components/ui';
import { changePassword, updateOwnProfile } from '@/lib/account-api';
import { useAuth } from '@/lib/auth-context';
import type { AdminProfile } from '@/lib/session';
import { meetsPasswordPolicy } from '@/lib/password';
import { humanize } from '@/lib/status';
import { errorMessage, fieldErrors } from '@/lib/utils';

export function ProfilePage() {
  const { user } = useAuth();

  // RequireAuth guarantees a user; this only narrows the type.
  if (!user) return null;

  return (
    <>
      <PageHeader title="My Profile" description="Your account details and password." />

      <div className="grid max-w-4xl gap-5 lg:grid-cols-2 lg:items-start">
        {/* Keyed by id so a different account never inherits the old form state. */}
        <DetailsCard key={user.id} user={user} />
        <PasswordCard />
      </div>
    </>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 text-sm">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="truncate text-right font-medium text-ink">{value}</dd>
    </div>
  );
}

function DetailsCard({ user }: { user: AdminProfile }) {
  const { updateUser } = useAuth();

  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const dirty = firstName.trim() !== user.firstName || lastName.trim() !== user.lastName;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);

    const local: Record<string, string> = {};
    if (!firstName.trim()) local.firstName = 'First name is required.';
    if (!lastName.trim()) local.lastName = 'Last name is required.';
    setErrors(local);
    if (Object.keys(local).length > 0) return;

    setSaving(true);

    try {
      const profile = await updateOwnProfile(firstName.trim(), lastName.trim());
      // The header reads the name from context; refresh it there too.
      updateUser(profile);
      setFirstName(profile.firstName);
      setLastName(profile.lastName);
      setNotice({ tone: 'success', text: 'Your details have been saved.' });
    } catch (cause) {
      setErrors(fieldErrors(cause));
      setNotice({ tone: 'danger', text: errorMessage(cause, 'Could not save your details.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Your details" />
      <form onSubmit={handleSubmit} className="space-y-4 p-5" noValidate>
        {notice && <FormNotice tone={notice.tone}>{notice.text}</FormNotice>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" htmlFor="firstName" required error={errors.firstName}>
            <Input
              id="firstName"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              maxLength={80}
              autoComplete="given-name"
              invalid={Boolean(errors.firstName)}
            />
          </Field>
          <Field label="Last name" htmlFor="lastName" required error={errors.lastName}>
            <Input
              id="lastName"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              maxLength={80}
              autoComplete="family-name"
              invalid={Boolean(errors.lastName)}
            />
          </Field>
        </div>

        <dl className="divide-y divide-line rounded-lg border border-line px-3">
          <ReadOnlyRow label="Email" value={user.email} />
          <ReadOnlyRow label="Mobile" value={user.mobile ?? '—'} />
          <ReadOnlyRow label="Role" value={humanize(user.role)} />
          <ReadOnlyRow label="Status" value={humanize(user.status)} />
        </dl>
        <p className="text-xs text-ink-subtle">
          Email and mobile are sign-in identities. Ask an administrator to change them.
        </p>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving || !dirty}>
            {saving && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const { clearSession } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!current) {
      setError('Enter your current password.');
      return;
    }
    if (!meetsPasswordPolicy(next)) {
      setError('Your new password does not meet the requirements below.');
      return;
    }
    if (next !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    if (next === current) {
      setError('The new password must differ from the current one.');
      return;
    }

    setSaving(true);

    try {
      await changePassword(current, next);
      // The server has revoked every session, this one included — any further
      // request would 401. Leave cleanly and say why.
      clearSession();
      navigate('/login', {
        replace: true,
        state: { notice: 'Password changed. Sign in again with your new password.' },
      });
    } catch (cause) {
      setError(errorMessage(cause, 'Could not change your password.'));
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Change password"
        subtitle="You will be signed out everywhere, including here."
      />
      <form onSubmit={handleSubmit} className="space-y-4 p-5" noValidate>
        {error && <FormNotice tone="danger">{error}</FormNotice>}

        <Field label="Current password" htmlFor="currentPassword" required>
          <PasswordInput
            id="currentPassword"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
          />
        </Field>

        <Field label="New password" htmlFor="newPassword" required>
          <PasswordInput
            id="newPassword"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            autoComplete="new-password"
            aria-describedby="new-password-rules"
          />
          <PasswordRules id="new-password-rules" value={next} />
        </Field>

        <Field label="Confirm new password" htmlFor="confirmNewPassword" required>
          <PasswordInput
            id="confirmNewPassword"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            invalid={confirm.length > 0 && confirm !== next}
          />
        </Field>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {saving ? 'Changing…' : 'Change password'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
